import {
    Children,
    CSSProperties,
    FormEvent,
    Fragment,
    ReactNode,
    isValidElement,
    useMemo,
    useState,
} from 'react';
import './legacy-widgets.css';

/**
 * Local stand-ins for the Cloudscape widgets still used by the older
 * inspection, report, and admin screens. These do not import Cloudscape.
 */

const SPACE: Record<string, number> = {
    xxxs: 2, xxs: 4, xs: 8, s: 12, m: 16, l: 20, xl: 24, xxl: 32,
};

type Space = { top?: string; bottom?: string; left?: string; right?: string };

function applySpace(style: CSSProperties, kind: 'margin' | 'padding', value?: Space | string) {
    if (!value) return;
    if (typeof value === 'string') {
        if (kind === 'margin') style.margin = SPACE[value];
        else style.padding = SPACE[value];
        return;
    }
    const sides = ['top', 'bottom', 'left', 'right'] as const;
    sides.forEach((side) => {
        const token = value[side];
        if (!token || SPACE[token] == null) return;
        const px = SPACE[token];
        if (kind === 'margin') {
            if (side === 'top') style.marginTop = px;
            if (side === 'bottom') style.marginBottom = px;
            if (side === 'left') style.marginLeft = px;
            if (side === 'right') style.marginRight = px;
        } else {
            if (side === 'top') style.paddingTop = px;
            if (side === 'bottom') style.paddingBottom = px;
            if (side === 'left') style.paddingLeft = px;
            if (side === 'right') style.paddingRight = px;
        }
    });
}

function spaceStyle(margin?: Space | string, padding?: Space | string): CSSProperties {
    const style: CSSProperties = {};
    applySpace(style, 'margin', margin);
    applySpace(style, 'padding', padding);
    return style;
}

const ICONS: Record<string, string> = {
    remove: '×',
    close: '×',
    'add-plus': '+',
    'caret-up-filled': '▲',
    'caret-down-filled': '▼',
    edit: '✎',
    envelope: '✉',
    microphone: '🎤',
    'microphone-off': '🎤',
    'status-positive': '✓',
    'status-warning': '!',
    'status-negative': '×',
};

export function Icon({
    name,
    url,
    size = 'medium',
    children,
}: {
    name?: string;
    url?: string;
    size?: string;
    variant?: string;
    children?: ReactNode;
}) {
    const px = size === 'large' || size === 'big' ? 28 : size === 'small' ? 14 : 20;
    if (url) {
        return <img src={url} alt="" width={px} height={px} />;
    }
    return (
        <span aria-hidden="true" style={{ fontSize: px, lineHeight: 1 }}>
            {ICONS[name || ''] || children || '•'}
        </span>
    );
}

export function Button({
    children,
    onClick,
    disabled,
    variant,
    iconName,
    iconAlign = 'left',
    formAction,
    loading,
    type,
    'aria-label': ariaLabel,
    ariaLabel: ariaLabelCamel,
}: {
    children?: ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    variant?: string;
    iconName?: string;
    iconAlign?: 'left' | 'right';
    formAction?: string;
    loading?: boolean;
    loadingText?: string;
    type?: 'button' | 'submit' | 'reset';
    'aria-label'?: string;
    ariaLabel?: string;
    [key: string]: unknown;
}) {
    const icon = iconName ? <Icon name={iconName} /> : null;
    return (
        <button
            type={type || (formAction === 'none' ? 'button' : 'button')}
            className={`legacy-button${variant === 'primary' ? ' legacy-button--primary' : ''}${variant === 'link' || variant === 'inline-link' ? ' legacy-button--link' : ''}${variant === 'icon' ? ' legacy-button--icon' : ''}`}
            onClick={onClick}
            disabled={disabled || loading}
            aria-label={ariaLabel || ariaLabelCamel}
        >
            {iconAlign !== 'right' && icon}
            {children}
            {iconAlign === 'right' && icon}
        </button>
    );
}

export function Box({
    children,
    variant,
    textAlign,
    float,
    color,
    fontWeight,
    margin,
    padding,
    display,
}: {
    children?: ReactNode;
    variant?: string;
    textAlign?: string;
    float?: string;
    color?: string;
    fontSize?: string;
    fontWeight?: string;
    margin?: Space | string;
    padding?: Space | string;
    display?: string;
    [key: string]: unknown;
}) {
    const Tag = variant === 'h1' || variant === 'h2' || variant === 'h3' ? variant : variant === 'h5' ? 'h3' : variant === 'p' || variant === 'small' ? 'p' : variant === 'span' ? 'span' : 'div';
    const className = [
        variant === 'small' || color === 'text-body-secondary' ? 'legacy-muted' : '',
        textAlign === 'center' ? 'legacy-center' : '',
        float === 'right' ? 'legacy-float-right' : '',
        color === 'text-status-success' ? 'legacy-success' : '',
        color === 'text-status-warning' ? 'legacy-warning' : '',
        fontWeight === 'bold' || fontWeight === 'heavy' ? 'legacy-bold' : '',
    ].filter(Boolean).join(' ');
    return (
        <Tag className={className} style={{ ...spaceStyle(margin, padding), display, fontWeight: fontWeight === 'bold' || fontWeight === 'heavy' ? 700 : undefined }}>
            {children}
        </Tag>
    );
}

export function Header({
    children,
    variant = 'h2',
    actions,
    description,
    counter,
}: {
    children?: ReactNode;
    variant?: string;
    actions?: ReactNode;
    description?: ReactNode;
    counter?: ReactNode;
}) {
    const Tag = variant === 'h1' ? 'h1' : variant === 'h3' ? 'h3' : 'h2';
    return (
        <div className="legacy-header">
            <div>
                <Tag>
                    {children}
                    {counter ? <span> {counter}</span> : null}
                </Tag>
                {description}
            </div>
            {actions ? <div>{actions}</div> : null}
        </div>
    );
}

export function Container({
    children,
    header,
    className,
}: {
    children?: ReactNode;
    header?: ReactNode;
    className?: string;
}) {
    return (
        <section className={`legacy-container ${className || ''}`}>
            {header}
            {children}
        </section>
    );
}

export function Form({ children, actions }: { children?: ReactNode; actions?: ReactNode }) {
    return (
        <form
            onSubmit={(event: FormEvent) => {
                event.preventDefault();
            }}
        >
            {children}
            {actions ? <div className="legacy-form-actions">{actions}</div> : null}
        </form>
    );
}

export function FormField({
    label,
    description,
    children,
    constraintText,
    errorText,
}: {
    label?: ReactNode;
    description?: ReactNode;
    children?: ReactNode;
    stretch?: boolean;
    constraintText?: ReactNode;
    errorText?: ReactNode;
}) {
    return (
        <div className="legacy-field">
            {label ? <label>{label}</label> : null}
            {description ? <div className="legacy-field-note">{description}</div> : null}
            {children}
            {constraintText ? <div className="legacy-muted">{constraintText}</div> : null}
            {errorText ? <div className="legacy-warning">{errorText}</div> : null}
        </div>
    );
}

export function Input({
    value,
    onChange,
    type = 'text',
    placeholder,
    autoComplete,
    autoFocus,
    inputMode,
    disabled,
}: {
    value?: string | number;
    onChange?: (event: { detail: { value: string } }) => void;
    type?: string;
    placeholder?: string;
    autoComplete?: boolean | string;
    autoFocus?: boolean;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
    disabled?: boolean;
}) {
    // Cloudscape accepted a boolean here (false = disable autofill). Map to the
    // HTML attribute: false → "off", true → "on", string passes through.
    const autoCompleteAttr =
        typeof autoComplete === 'boolean'
            ? autoComplete
                ? 'on'
                : 'off'
            : autoComplete;
    return (
        <input
            className="legacy-input"
            type={type}
            value={value ?? ''}
            placeholder={placeholder}
            autoComplete={autoCompleteAttr}
            autoFocus={autoFocus}
            inputMode={inputMode}
            disabled={disabled}
            onChange={(event) => onChange?.({ detail: { value: event.target.value } })}
        />
    );
}

export function Textarea({
    value,
    onChange,
    autoFocus,
    placeholder,
}: {
    value?: string;
    onChange?: (event: { detail: { value: string } }) => void;
    autoFocus?: boolean;
    placeholder?: string;
}) {
    return (
        <textarea
            className="legacy-textarea"
            value={value ?? ''}
            placeholder={placeholder}
            autoFocus={autoFocus}
            rows={4}
            onChange={(event) => onChange?.({ detail: { value: event.target.value } })}
        />
    );
}

type Option = { label?: string; value?: string; disabled?: boolean };

export function Select({
    selectedOption,
    options = [],
    onChange,
    placeholder,
    disabled,
}: {
    selectedOption?: Option | null;
    options?: Option[];
    onChange?: (event: { detail: { selectedOption: Option } }) => void;
    placeholder?: string;
    disabled?: boolean;
    [key: string]: unknown;
}) {
    return (
        <select
            className="legacy-select"
            disabled={disabled}
            value={selectedOption?.value ?? ''}
            onChange={(event) => {
                const next = options.find((option) => option.value === event.target.value) || { label: '', value: '' };
                onChange?.({ detail: { selectedOption: next } });
            }}
        >
            <option value="">{placeholder || 'Select'}</option>
            {options.map((option) => (
                <option key={option.value || option.label} value={option.value} disabled={option.disabled}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

export function Checkbox({
    checked,
    onChange,
    children,
}: {
    checked?: boolean;
    onChange?: (event: { detail: { checked: boolean } }) => void;
    children?: ReactNode;
    indeterminate?: boolean;
    [key: string]: unknown;
}) {
    return (
        <label className="legacy-field">
            <span>
                <input
                    type="checkbox"
                    checked={!!checked}
                    onChange={(event) => onChange?.({ detail: { checked: event.target.checked } })}
                />{' '}
                {children}
            </span>
        </label>
    );
}

export function Modal({
    visible = false,
    onDismiss,
    header,
    footer,
    children,
    size,
}: {
    visible?: boolean;
    onDismiss?: () => void;
    header?: ReactNode;
    footer?: ReactNode;
    children?: ReactNode;
    size?: string;
}) {
    if (!visible) return null;
    return (
        <div className="legacy-modal-overlay" onClick={onDismiss}>
            <div
                className={`legacy-modal-panel${size === 'max' || size === 'large' ? ' legacy-modal-panel--max' : ''}`}
                role="dialog"
                aria-modal="true"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="legacy-header">
                    <h2>{header}</h2>
                    <Button variant="icon" aria-label="Close" onClick={onDismiss}>×</Button>
                </div>
                <div>{children}</div>
                {footer ? <div className="legacy-modal-footer">{footer}</div> : null}
            </div>
        </div>
    );
}

export function Grid({
    gridDefinition = [],
    children,
}: {
    gridDefinition?: { colspan?: { default?: number; xxs?: number; xs?: number; s?: number } }[];
    children?: ReactNode;
    [key: string]: unknown;
}) {
    return (
        <div className="legacy-grid">
            {Children.map(children, (child, index) => {
                const span = gridDefinition[index]?.colspan?.s || gridDefinition[index]?.colspan?.xs || gridDefinition[index]?.colspan?.default || 12;
                return <div style={{ gridColumn: `span ${Math.min(span, 12)}` }}>{child}</div>;
            })}
        </div>
    );
}

export function ColumnLayout({
    columns = 2,
    children,
}: {
    columns?: number;
    variant?: string;
    borders?: string;
    children?: ReactNode;
}) {
    return (
        <div className="legacy-columns" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {children}
        </div>
    );
}

// These namespaces intentionally mirror Cloudscape's public type API
// (e.g. `TableProps.ColumnDefinition`, `TabsProps.ChangeDetail`) so the consuming
// screens keep their existing type references unchanged. Prefer namespaces here
// over restructuring every consumer.
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace TableProps {
    export interface ColumnDefinition<T> {
        id?: string;
        header: ReactNode;
        cell: (item: T) => ReactNode;
        width?: number | string;
        minWidth?: number | string;
        sortingField?: string;
        [key: string]: unknown;
    }
}

export function Table<T>({
    columnDefinitions = [],
    items = [],
    header,
    footer,
    empty,
    loading,
    stripedRows,
    trackBy = 'id',
    pagination,
    preferences,
    filter,
    variant,
    columnDisplay,
    expandableRows,
}: {
    columnDefinitions?: TableProps.ColumnDefinition<T>[];
    items?: readonly T[];
    header?: ReactNode;
    footer?: ReactNode;
    empty?: ReactNode;
    loading?: boolean;
    stripedRows?: boolean;
    wrapLines?: boolean;
    trackBy?: string | ((item: T) => string);
    pagination?: ReactNode;
    preferences?: ReactNode;
    filter?: ReactNode;
    variant?: string;
    selectionType?: string;
    selectedItems?: readonly T[];
    onSelectionChange?: (event: { detail: { selectedItems: T[] } }) => void;
    columnDisplay?: { id: string; visible?: boolean }[];
    expandableRows?: {
        getItemChildren: (item: T) => readonly T[];
        isItemExpandable: (item: T) => boolean;
        expandedItems: readonly T[];
        onExpandableItemToggle: (event: { detail: { item: T; expanded: boolean } }) => void;
    };
    [key: string]: unknown;
}) {
    const rows = items || [];
    const columns = columnDisplay?.length
        ? columnDefinitions.filter((column) => columnDisplay.some((entry) => entry.id === column.id && entry.visible !== false))
        : columnDefinitions;
    const rowKey = (item: T, index: number) => (
        typeof trackBy === 'function'
            ? trackBy(item)
            : String((item as Record<string, unknown>)?.[trackBy] ?? index)
    );
    const renderRows = (list: readonly T[]): ReactNode => list.map((item, index) => {
        const key = rowKey(item, index);
        const children = expandableRows?.getItemChildren(item) || [];
        const expanded = !!expandableRows?.expandedItems?.some((expandedItem) => rowKey(expandedItem, index) === key);
        return (
            <Fragment key={key}>
                <tr>
                    {expandableRows ? (
                        <td>
                            {expandableRows.isItemExpandable(item) ? (
                                <button
                                    type="button"
                                    className="legacy-button legacy-button--icon"
                                    aria-expanded={expanded}
                                    onClick={() => expandableRows.onExpandableItemToggle({ detail: { item, expanded: !expanded } })}
                                >
                                    {expanded ? '▼' : '▶'}
                                </button>
                            ) : null}
                        </td>
                    ) : null}
                    {columns.map((column) => (
                        <td key={column.id || String(column.header)}>{column.cell(item)}</td>
                    ))}
                </tr>
                {expanded ? renderRows(children) : null}
            </Fragment>
        );
    });
    return (
        <div className="legacy-table-wrap">
            {header}
            {filter}
            {loading ? <Spinner /> : null}
            {!loading && rows.length === 0 ? empty : null}
            {rows.length > 0 && (
                <table className={`legacy-table${stripedRows ? ' legacy-table--striped' : ''}${variant ? '' : ''}`}>
                    <thead>
                        <tr>
                            {expandableRows ? <th /> : null}
                            {columns.map((column) => (
                                <th key={column.id || String(column.header)}>{column.header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{renderRows(rows)}</tbody>
                </table>
            )}
            {footer}
            <div className="legacy-header">
                {pagination}
                {preferences}
            </div>
        </div>
    );
}

export function Spinner({ size = 'normal' }: { size?: string; [key: string]: unknown }) {
    const px = size === 'big' || size === 'large' ? 32 : size === 'small' ? 14 : 20;
    return (
        <div
            className="legacy-spinner"
            role="status"
            aria-label="Loading"
            style={{ width: px, height: px }}
        />
    );
}

export function Link({
    children,
    href,
    onFollow,
    onClick,
    external,
}: {
    children?: ReactNode;
    href?: string;
    onFollow?: (event: { preventDefault: () => void }) => void;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    external?: boolean;
    color?: string;
    variant?: string;
}) {
    return (
        <a
            href={href || '#'}
            target={external ? '_blank' : undefined}
            rel={external ? 'noreferrer' : undefined}
            onClick={(event) => {
                if (onFollow) {
                    event.preventDefault();
                    onFollow({ preventDefault: () => event.preventDefault() });
                }
                onClick?.(event);
            }}
        >
            {children}
        </a>
    );
}

export function ProgressBar({ value = 0 }: { value?: number; label?: string }) {
    return (
        <div className="legacy-progress" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} role="progressbar">
            <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
        </div>
    );
}

export function Badge({ children, color }: { children?: ReactNode; color?: string }) {
    const tone = color === 'green' ? 'green' : color === 'red' ? 'red' : color === 'blue' ? 'blue' : 'grey';
    return <span className={`legacy-badge legacy-badge--${tone}`}>{children}</span>;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FlashbarProps {
    export interface MessageDefinition {
        id?: string;
        type?: 'success' | 'error' | 'warning' | 'info';
        header?: ReactNode;
        content?: ReactNode;
        dismissible?: boolean;
        onDismiss?: () => void;
    }
}

export function Flashbar({ items = [] }: { items?: FlashbarProps.MessageDefinition[] }) {
    if (!items.length) return null;
    return (
        <div>
            {items.map((item) => (
                <div key={item.id || String(item.header)} className={`legacy-flash legacy-flash--${item.type || 'info'}`} role="status">
                    {item.header ? <strong>{item.header}</strong> : null}
                    <div>{item.content}</div>
                    {item.dismissible ? <Button variant="link" onClick={item.onDismiss}>Dismiss</Button> : null}
                </div>
            ))}
        </div>
    );
}

export function Alert({
    children,
    header,
    type = 'info',
}: {
    children?: ReactNode;
    header?: ReactNode;
    type?: string;
    statusIconAriaLabel?: string;
}) {
    return (
        <div className={`legacy-alert legacy-alert--${type}`} role="status">
            {header ? <strong>{header}</strong> : null}
            <div>{children}</div>
        </div>
    );
}

export function StatusIndicator({ type = 'info', children }: { type?: string; children?: ReactNode }) {
    return <span className={`legacy-status legacy-status--${type}`}>{children}</span>;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace TabsProps {
    export interface ChangeDetail {
        activeTabId: string;
    }
    export interface Tab {
        id: string;
        label: ReactNode;
        content?: ReactNode;
    }
}

export function Tabs({
    tabs = [],
    activeTabId,
    onChange,
}: {
    tabs?: TabsProps.Tab[];
    activeTabId?: string;
    onChange?: (event: { detail: TabsProps.ChangeDetail }) => void;
    variant?: string;
}) {
    const [localId, setLocalId] = useState(tabs[0]?.id);
    const current = activeTabId || localId;
    const active = tabs.find((tab) => tab.id === current) || tabs[0];
    return (
        <div>
            <div className="legacy-tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={tab.id === active?.id}
                        className={`legacy-tab${tab.id === active?.id ? ' is-active' : ''}`}
                        onClick={() => {
                            setLocalId(tab.id);
                            onChange?.({ detail: { activeTabId: tab.id } });
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            {active?.content}
        </div>
    );
}

export function Pagination({
    currentPageIndex = 1,
    pagesCount = 1,
    onChange,
}: {
    currentPageIndex?: number;
    pagesCount?: number;
    onChange?: (event: { detail: { currentPageIndex: number } }) => void;
}) {
    return (
        <div>
            <Button
                disabled={currentPageIndex <= 1}
                onClick={() => onChange?.({ detail: { currentPageIndex: currentPageIndex - 1 } })}
            >
                Previous
            </Button>
            <span>
                {currentPageIndex} / {pagesCount}
            </span>
            <Button
                disabled={currentPageIndex >= pagesCount}
                onClick={() => onChange?.({ detail: { currentPageIndex: currentPageIndex + 1 } })}
            >
                Next
            </Button>
        </div>
    );
}

export function CollectionPreferences({
    title = 'Preferences',
    confirmLabel = 'Confirm',
    preferences,
    pageSizePreference,
    onConfirm,
}: {
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    preferences?: { pageSize?: number; visibleContent?: string[] };
    pageSizePreference?: { title?: string; options?: { value: number; label: string }[] };
    onConfirm?: (event: { detail: { pageSize?: number; visibleContent?: string[] } }) => void;
}) {
    const [pageSize, setPageSize] = useState(preferences?.pageSize || pageSizePreference?.options?.[0]?.value);
    return (
        <details>
            <summary>{title}</summary>
            <div className="legacy-field">
                <label>{pageSizePreference?.title || 'Page size'}</label>
                <select className="legacy-select" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                    {pageSizePreference?.options?.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
                <Button
                    variant="primary"
                    onClick={() => onConfirm?.({ detail: { pageSize, visibleContent: preferences?.visibleContent } })}
                >
                    {confirmLabel}
                </Button>
            </div>
        </details>
    );
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace DateRangePickerProps {
    export type AbsoluteValue = { type: 'absolute'; startDate: string; endDate: string };
    export type RelativeValue = { type: 'relative'; amount: number; unit: string; key?: string };
    export type Value = AbsoluteValue | RelativeValue;
}

export function DateRangePicker({
    value,
    onChange,
    placeholder,
    relativeOptions = [],
}: {
    value?: DateRangePickerProps.Value | null;
    onChange?: (event: { detail: { value: DateRangePickerProps.Value | null } }) => void;
    placeholder?: string;
    relativeOptions?: { key: string; amount: number; unit: string; type: string }[];
    isValidRange?: (range: DateRangePickerProps.Value | null) => { valid: boolean; errorMessage?: string };
    i18nStrings?: {
        formatRelativeRange?: (value: { amount: number; unit: string }) => string;
        formatUnit?: (unit: string, amount: number) => string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}) {
    const absolute = value?.type === 'absolute' ? value : null;
    const [start, setStart] = useState(absolute?.startDate?.slice(0, 10) || '');
    const [end, setEnd] = useState(absolute?.endDate?.slice(0, 10) || '');
    const apply = () => {
        if (!start || !end) return;
        onChange?.({ detail: { value: { type: 'absolute', startDate: start, endDate: end } } });
    };
    return (
        <div className="legacy-field">
            <span className="legacy-muted">{placeholder}</span>
            <select
                className="legacy-select"
                value={value?.type === 'relative' ? value.key || '' : ''}
                onChange={(event) => {
                    const option = relativeOptions.find((item) => item.key === event.target.value);
                    if (!option) {
                        onChange?.({ detail: { value: null } });
                        return;
                    }
                    onChange?.({
                        detail: {
                            value: { type: 'relative', amount: option.amount, unit: option.unit, key: option.key },
                        },
                    });
                }}
            >
                <option value="">Custom dates</option>
                {relativeOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                        Last {option.amount} {option.unit}
                    </option>
                ))}
            </select>
            <input className="legacy-input" type="date" value={start} onChange={(event) => setStart(event.target.value)} />
            <input className="legacy-input" type="date" value={end} onChange={(event) => setEnd(event.target.value)} />
            <Button variant="primary" onClick={apply}>Apply</Button>
            <Button onClick={() => onChange?.({ detail: { value: null } })}>Clear</Button>
        </div>
    );
}

export function FileUpload({
    value = [],
    onChange,
    accept,
    multiple,
    constraintText,
}: {
    value?: readonly File[];
    onChange?: (event: { detail: { value: File[] } }) => void;
    accept?: string;
    multiple?: boolean;
    constraintText?: string;
    i18nStrings?: {
        uploadButtonText?: (multiple: boolean) => string;
        dropzoneText?: (multiple: boolean) => string;
        removeFileAriaLabel?: (index: number) => string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}) {
    return (
        <div className="legacy-file">
            <input
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={(event) => onChange?.({ detail: { value: Array.from(event.target.files || []) } })}
            />
            {value.length > 0 ? <div className="legacy-muted">{value.map((file) => file.name).join(', ')}</div> : null}
            {constraintText ? <div className="legacy-muted">{constraintText}</div> : null}
        </div>
    );
}

export function Calendar({
    value,
    onChange,
}: {
    value?: string;
    onChange?: (event: { detail: { value: string } }) => void;
    granularity?: string;
}) {
    const current = value ? new Date(`${value}T00:00:00`) : new Date();
    const year = current.getFullYear();
    const month = current.getMonth();
    const days = useMemo(() => {
        const count = new Date(year, month + 1, 0).getDate();
        return Array.from({ length: count }, (_, index) => index + 1);
    }, [year, month]);
    return (
        <div>
            <div className="legacy-header">
                <strong>{current.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</strong>
            </div>
            <div className="legacy-calendar">
                {days.map((day) => {
                    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    return (
                        <button
                            key={iso}
                            type="button"
                            className={iso === value ? 'is-selected' : ''}
                            onClick={() => onChange?.({ detail: { value: iso } })}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function ButtonDropdown({
    items = [],
    onItemClick,
    children,
}: {
    items?: {
        id?: string;
        text?: ReactNode;
        disabled?: boolean;
        items?: { id: string; text?: ReactNode; disabled?: boolean; key?: string }[];
    }[];
    onItemClick?: (event: { detail: { id: string } }) => void;
    children?: ReactNode;
    [key: string]: unknown;
}) {
    const choices = items.flatMap((item) => (
        item.items?.length
            ? item.items
            : item.id
                ? [{ id: item.id, text: item.text, disabled: item.disabled }]
                : []
    ));
    return (
        <select
            className="legacy-select"
            defaultValue=""
            aria-label={typeof children === 'string' ? children : 'Choose'}
            onChange={(event) => {
                if (event.target.value) onItemClick?.({ detail: { id: event.target.value } });
            }}
        >
            <option value="">{isValidElement(children) ? 'Choose' : children || 'Choose'}</option>
            {choices.map((item) => (
                <option key={item.id} value={item.id} disabled={item.disabled}>
                    {item.text || item.id}
                </option>
            ))}
        </select>
    );
}

export function TopNavigation({
    identity,
    utilities = [],
}: {
    identity?: { href?: string; title?: string; onFollow?: (event: { preventDefault: () => void }) => void };
    utilities?: {
        type?: string;
        text?: string;
        items?: { id: string; text?: string }[];
        onItemClick?: (event: { detail: { id: string } }) => void;
    }[];
    i18nStrings?: Record<string, string>;
}) {
    return (
        <header className="legacy-topnav">
            <a
                href={identity?.href || '/'}
                onClick={(event) => {
                    if (!identity?.onFollow) return;
                    event.preventDefault();
                    identity.onFollow({ preventDefault: () => event.preventDefault() });
                }}
            >
                {identity?.title || 'Home'}
            </a>
            {utilities.map((utility) => (
                <ButtonDropdown
                    key={utility.text}
                    items={utility.items}
                    onItemClick={utility.onItemClick}
                >
                    {utility.text || 'Menu'}
                </ButtonDropdown>
            ))}
        </header>
    );
}
